// The bank directory (the Olene account's, through GET /bank-directory; backend/bank_fraction.py) for bank
// forms: suggestions while typing a bank's name, each a routing number with its fractional number. Any
// other bank can be typed. The same logic as olene-platform apps/account-web/src/lib/bankDirectory.ts.
import { useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL;
let cached = null;

export function useBankDirectory(token) {
  const [banks, setBanks] = useState([]);
  useEffect(() => {
    if (!token) return;
    cached = cached || fetch(`${API_URL}/bank-directory`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { banks: [] })).then((d) => d.banks || [])
      .catch(() => { cached = null; return []; });
    cached.then(setBanks);
  }, [token]);
  return banks;
}

const STATES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", CO: "Colorado", CT: "Connecticut", DE: "Delaware",
  DC: "Washington, D.C.", FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas",
  KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
  MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York",
  NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island",
  SC: "South Carolina", SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
  WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/** Matches for what's typed; the company's own state (e.g. "TN") first. */
export function suggest(banks, typed, companyState, limit = 5) {
  const words = norm(typed).split(" ").filter(Boolean);
  if (!words.length || (words.length === 1 && words[0].length < 2)) return [];
  const home = companyState ? STATES[String(companyState).toUpperCase()] : undefined;
  const out = [];
  for (const b of banks) {
    const names = norm([b.name, ...(b.aliases || [])].join(" "));
    for (const r of b.routes || []) {
      const hay = `${names} ${norm(r.states.join(" "))} ${r.routing}`;
      if (!words.every((w) => hay.includes(w))) continue;
      // An older number (legacy) only when its digits are typed: it's there to fill the fraction.
      if (r.legacy && !words.some((w) => /^\d{4,}$/.test(w) && r.routing.includes(w))) continue;
      const rank = home && r.states.some((s) => s.startsWith(home)) ? 0 : 1;
      out.push({ bankName: b.name, place: b.place, routing: r.routing, fraction: r.fraction, states: r.states, rank });
    }
  }
  return out.sort((a, b) => a.rank - b.rank || a.bankName.localeCompare(b.bankName) || a.states[0].localeCompare(b.states[0]))
    .slice(0, limit);
}

/** The directory's fraction for a routing number someone typed. */
export function fractionFor(banks, routing) {
  for (const b of banks) {
    const r = (b.routes || []).find((x) => x.routing === routing);
    if (r) return r.fraction;
  }
  return undefined;
}
